<script setup lang="ts">
import { IonPage, modalController } from "@ionic/vue";
import { computed, defineProps, onMounted, ref, withDefaults } from "vue";
import { useToggle } from "@vueuse/core";
import wordShow from "./wordShow.vue";
import AudioComp from "@/views/AIGenerate/components/AudioComp.vue";

interface RootJsonType {
  wordRoot: string;
  rootchinesedefinition: string;
  rootchineseDefinition: string;
  rootphonetic: string;
  words: WordJsonType[];
}
interface WordJsonType {
  index: string;
  word: string;
  chinesedefinition: string;
  chineseDefinition: string;
  phonetic: string;
}

const props = withDefaults(
  defineProps<{
    name: string;
    resJson?: RootJsonType;
  }>(),
  {
    name: "",
    resJson: () => ({
      wordRoot: "",
      rootchineseDefinition: "",
      rootphonetic: "",
      words: [],
    }),
  }
);

const [isAnimating, changeIsAnimating] = useToggle();
const currentList = ref<WordJsonType[]>();
const currentIndex = ref(0);
function close() {
  modalController.dismiss();
}

const resJson = computed(() => props.resJson);
const maxIndexLength = computed(() => Math.ceil(resJson.value.words.length / 4));
onMounted(() => {
  getCurrentList(currentIndex.value);
});

function getCurrentList(index: number) {
  currentIndex.value = index;
  const list = resJson.value.words;
  currentList.value = list.slice(index * 4, index * 4 + 4);
}
function changeStep(type: "next" | "back") {
  if (
    (type === "back" && currentIndex.value <= 0) ||
    (type === "next" && currentIndex.value >= maxIndexLength.value - 1) ||
    isAnimating.value
  ) {
    return;
  }
  changeIsAnimating(true);
  setTimeout(() => {
    getCurrentList(type === "next" ? currentIndex.value + 1 : currentIndex.value - 1);
  }, 300);
}
function handlePhonetic(phonetic: string) {
  const regex = /^(?:\/(.*)\/|\[(.*)])$/;
  if (!regex.test(phonetic)) {
    return `[${phonetic}]`;
  }
  return phonetic ? phonetic.replace(/^\/(.+)\/$/g, "[$1]") : "";
}
</script>

<template>
  <IonPage>
    <div class="container">
      <div class="header">
        <!--        <div class="header-title">
          {{ props.name }}
        </div>
        <i class="iconfont icon-guanbi-xiao header-close" @click="close" />-->
        <div class="header-title">
          <div style="margin-bottom: 0.4rem">{{ props.name }}</div>
          <div v-if="resJson" class="word">
            <span style="color: #ff5323; margin-right: 0.5rem">{{ resJson.wordRoot }}</span>
            <div class="phonetic">
              <span>[{{ resJson.rootphonetic }}]</span>
              <AudioComp :word="resJson.wordRoot" class="audio" />
            </div>
          </div>
          <div class="header-description">
            {{ resJson.rootchinesedefinition ?? resJson.rootchineseDefinition }}
          </div>
        </div>
        <i class="iconfont icon-guanbi-xiao header-close" @click="close" />
      </div>
      <div class="content">
        <div
          class="content-left"
          :class="[currentIndex <= 0 && 'content-active']"
          @click="changeStep('back')"
        >
          <i class="iconfont icon-back" />
        </div>
        <div class="content-center">
          <div class="content-center-box">
            <word-show v-if="resJson.wordRoot" :name="resJson.wordRoot" :root="resJson.wordRoot" />
          </div>
          <div
            class="content-center-out"
            :class="[isAnimating && 'animation']"
            @animationend="changeIsAnimating(false)"
          >
            <div v-for="item in currentList" :key="item" class="content-center-out_item">
              <word-show
                v-if="item['单词']"
                :name="item['单词']"
                :phonetic="handlePhonetic(item['音标'])"
                :definition="item['翻译']"
                :root="resJson.wordRoot"
              />
            </div>
          </div>
        </div>
        <div
          class="content-right"
          :class="[currentIndex >= maxIndexLength - 1 && 'content-active']"
          @click="changeStep('next')"
        >
          <i class="iconfont icon-back" />
        </div>
      </div>
      <div />
    </div>
  </IonPage>
</template>

<style scoped lang="less">
.container {
  background: #ffffff;
  border-radius: 0.5rem 0.5rem 0 0;
  height: 60%;
  box-sizing: border-box;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  .header {
    position: relative;
    color: #2a1f10;
    display: flex;
    justify-content: space-between;
    align-items: center;
    &-title {
      font-size: 1.3rem;
      font-weight: 600;
      .word {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        margin: 0.2rem 0;
        font-size: 1.3rem;
      }
      .header-description {
        color: #7e6950;
        font-weight: 400;
        font-size: 0.9rem;
      }
      .phonetic {
        padding: 0.2rem 0.4rem;
        border-radius: 1rem;
        background: #fccda2;
        color: #101214;
        font-size: 1rem;
        line-height: 1.2;
        font-weight: 400;
        display: inline-flex;
        .bofang-icon {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 1.21rem;
        }
        .audio {
          font-size: 1rem;
        }
      }
    }
    &-close {
      position: absolute;
      top: 0;
      right: 0;
      font-weight: 400;
    }
  }
  .content {
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-image: url("@/assets/root-bg.png");
    background-size: 100% 100%;
    background-repeat: no-repeat;
    &-left,
    &-right {
      height: 1.8rem;
      width: 1.8rem;
      background: #f9f3ee;
      color: #916f46;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;
      bottom: -42%;
      i {
        font-weight: bold;
        font-size: 1.5rem;
      }
    }
    &-right {
      transform: rotate(180deg);
    }
    &-active {
      i {
        color: #916f4655;
      }
    }
    &-center {
      position: relative;
      width: 100%;
      height: 100%;
      &-box {
        width: 8rem;
        height: 8rem;
        z-index: 999;
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
      }
      &-out {
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        &_item {
          position: absolute;
          max-width: 8.5rem;
        }
        &_item:nth-child(1) {
          top: 13%;
          left: 50%;
          transform: translateX(-50%);
          z-index: 1000;
        }
        &_item:nth-child(2) {
          top: 50%;
          right: -1.8rem;
          transform: translateY(-50%);
          :deep(.phonetic) {
            width: 100%;
            word-break: break-all;
            vertical-align: middle;
          }
        }
        &_item:nth-child(3) {
          bottom: 12%;
          left: 50%;
          transform: translateX(-50%);
        }
        &_item:nth-child(4) {
          top: 50%;
          left: -2rem;
          transform: translateY(-50%);
          :deep(.phonetic) {
            max-width: 9rem;
            word-break: break-all;
            vertical-align: middle;
          }
        }
      }
      .animation {
        animation: changeAnimation 1s ease-out;
      }
      @keyframes changeAnimation {
        0% {
          left: 0;
          top: 0;
          transform: rotate(0);
        }
        30% {
          transform: rotate(180deg);
          left: 0;
          top: 0;
        }
        60% {
          transform: rotate(360deg);
        }
        100% {
          left: 0;
          top: 0;
          transform: rotate(360deg);
        }
      }
    }
  }
}
</style>
